import {CSSProperties, useState} from "react";
import {FieldConstraint} from "../models/attributeManifest.model";
import {clampValue, needsClamp, describeConstraint} from "../utils/clamp";

interface NumberInputProps {
    value: number;
    step?: string;
    // Optional numeric range. Applied on commit (blur), NOT per keystroke, so you
    // can freely clear the field or type an out-of-range value and it snaps when
    // you leave it. Also fed to the native min/max/step for the spinner +
    // accessibility.
    constraint?: FieldConstraint | null;
    // When set, the value is shown rounded to this many decimals *at rest*, with
    // trailing zeros trimmed — so floating-point noise (e.g. 0.5333333333333333)
    // doesn't leak into the field. Safari renders the full-precision string;
    // Chromium shortens it — this normalizes both. The stored value is never
    // rounded: while focused the field shows the true value so edits are exact.
    // Leave undefined for integer fields (they display fine as-is).
    displayDecimals?: number;
    style?: CSSProperties;
    onChange(value: number): void;
}

function NumberInput(props: NumberInputProps) {
    const [focused, setFocused] = useState(false);
    // Raw text of the field while it's being edited, so a cleared or half-typed
    // entry is allowed to stay on screen instead of being coerced to a number on
    // every keystroke. Undefined when not editing — the field then mirrors
    // props.value.
    //
    // The draft is whatever the input itself reported (`e.target.value`), never a
    // string rebuilt from the number. On a type="number" field the browser reports
    // "" while the text is mid-entry and not yet a valid number ("", "-", "1.",
    // "1e"), and echoing that same "" back is a no-op write, which leaves the raw
    // text the user is typing untouched. Hand it a reconstructed number instead
    // and React writes to the DOM, wiping the character just typed.
    const [draft, setDraft] = useState<string | undefined>(undefined);

    const display = draft !== undefined
        ? draft
        : (props.displayDecimals !== undefined && !focused)
            ? format(props.value, props.displayDecimals)
            : props.value;

    const c = props.constraint;
    return (
        <input value={display} type={'number'}
               step={c?.Step ?? props.step ?? "1"}
               min={c?.Min ?? undefined}
               max={c?.Max ?? undefined}
               // Native hover tooltip on the input box itself (the label carries
               // the same range via NumberInputLabel's MUI tooltip).
               title={describeConstraint(c)}
               style={props.style}
               onFocus={() => setFocused(true)}
               onBlur={() => {
                   setFocused(false);
                   setDraft(undefined);   // stop drafting; show the stored value
                   // Clamp/normalize on commit (only if it actually changes, to
                   // avoid a redundant command + undo entry). An entry that never
                   // parsed committed nothing, so props.value is still the last
                   // good value and simply reappears here.
                   if (needsClamp(props.value, c)) props.onChange(clampValue(props.value, c));
               }}
               onChange={(v) => {
                   setDraft(v.target.value);
                   // Commit only a real number. An empty or half-typed entry is
                   // left alone rather than becoming 0 — that coercion is what
                   // made the field impossible to clear.
                   if (Number.isFinite(v.target.valueAsNumber)) props.onChange(v.target.valueAsNumber);
               }}/>
    )
}

// Round to at most `decimals` places and drop trailing zeros (and any float
// representation noise), returning a clean string for display.
function format(value: number, decimals: number): string {
    if (!Number.isFinite(value)) return String(value);
    return String(Number(value.toFixed(decimals)));
}

export default NumberInput;
